import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';

import { makeSelectHiddenGameSplashEnabled } from 'containers/Auth_old/selectors';

import type { DoomOverlayProps } from './doom/DoomOverlay';
import { loadDoom } from './loadDoom';
import { loadGame } from './loadGame';
import messages from './messages';
import {
  isHiddenGameSurfaceAvailable,
  openHiddenGame,
  setSplashOpener,
} from './openHiddenGame';
import { isTypingEvent } from './trigger/guards';
import { createWordMatcher } from './trigger/wordMatcher';

const selectEnabled = makeSelectHiddenGameSplashEnabled();

export function SplashHost() {
  const enabled = useSelector(selectEnabled);
  const [Game, setGame] = useState<React.ComponentType<
    import('./game').SplashGameProps
  > | null>(null);
  const [Doom, setDoom] =
    useState<React.ComponentType<DoomOverlayProps> | null>(null);
  const [failed, setFailed] = useState(false);
  const [doomFailed, setDoomFailed] = useState(false);
  const busy = useRef(false);
  const gameOpen = useRef(false);
  const generation = useRef(0);
  const [restoreFocusTo, setRestoreFocusTo] = useState<Element | null>(null);

  useEffect(() => {
    if (!enabled || !window.matchMedia('(any-pointer: fine)').matches) return;
    setSplashOpener(() => {
      if (busy.current || gameOpen.current) return false;
      busy.current = true;
      setFailed(false);
      setRestoreFocusTo(document.activeElement);
      generation.current += 1;
      const request = generation.current;
      void loadGame()
        .then(module => {
          if (request !== generation.current) return;
          if (!isHiddenGameSurfaceAvailable()) return;
          if (!module?.default) throw new Error('Missing game module');
          gameOpen.current = true;
          setGame(() => module.default);
        })
        .catch(() => {
          if (request === generation.current) {
            setFailed(true);
          }
        })
        .finally(() => {
          if (request === generation.current) busy.current = false;
        });
      return true;
    });
    const openDoom = (): boolean => {
      if (busy.current || gameOpen.current) return false;
      if (!isHiddenGameSurfaceAvailable()) return false;
      busy.current = true;
      setDoomFailed(false);
      setRestoreFocusTo(
        document.activeElement === document.body
          ? document.querySelector('main')
          : document.activeElement,
      );
      generation.current += 1;
      const request = generation.current;
      void loadDoom()
        .then(module => {
          if (request !== generation.current) return;
          if (!isHiddenGameSurfaceAvailable()) return;
          if (!module?.default) throw new Error('Missing DOOM module');
          gameOpen.current = true;
          setDoom(() => module.default);
        })
        .catch(() => {
          if (request === generation.current) setDoomFailed(true);
        })
        .finally(() => {
          if (request === generation.current) busy.current = false;
        });
      return true;
    };
    let matchWord = createWordMatcher({ word: 'hidden', gapMs: 1500 });
    let matchDoom = createWordMatcher({ word: 'iddqd', gapMs: 1500 });
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingEvent(event) || busy.current || gameOpen.current) {
        matchWord = createWordMatcher({ word: 'hidden', gapMs: 1500 });
        matchDoom = createWordMatcher({ word: 'iddqd', gapMs: 1500 });
        return;
      }
      if (matchWord(event)) openHiddenGame();
      else if (matchDoom(event)) openDoom();
    };
    const listenerOptions: AddEventListenerOptions = { passive: true };
    document.addEventListener('keydown', onKeyDown, listenerOptions);
    window.openHiddenGame = openHiddenGame;
    return () => {
      document.removeEventListener('keydown', onKeyDown, listenerOptions);
      generation.current += 1;
      busy.current = false;
      gameOpen.current = false;
      setGame(null);
      setDoom(null);
      setFailed(false);
      setDoomFailed(false);
      setSplashOpener();
      delete window.openHiddenGame;
    };
  }, [enabled]);

  useEffect(() => {
    if (!failed) return;
    const timer = window.setTimeout(() => setFailed(false), 4000);
    return () => window.clearTimeout(timer);
  }, [failed]);

  useEffect(() => {
    if (!doomFailed) return;
    const timer = window.setTimeout(() => setDoomFailed(false), 4000);
    return () => window.clearTimeout(timer);
  }, [doomFailed]);

  // DoomOverlay's effect depends on this; a new function each render would
  // restart its load timeout and pull focus back into the game.
  const closeDoom = useCallback(() => {
    gameOpen.current = false;
    setDoom(null);
  }, []);

  if (!enabled) return null;
  return (
    <>
      {(failed || doomFailed) && (
        <div
          role="alert"
          className="ds:box-border ds:h-9 ds:rounded-full ds:px-4 ds:text-lg ds:leading-9 ds:whitespace-nowrap ds:text-white"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            backgroundColor:
              'color-mix(in srgb, var(--ds-color-neutral-900) 90%, transparent)',
            fontWeight: 'var(--ds-font-weight-semibold)',
            zIndex: 1400,
          }}
        >
          <FormattedMessage
            {...(doomFailed ? messages.doomLoadError : messages.loadError)}
          />
        </div>
      )}
      {Doom && (
        <Sentry.ErrorBoundary onError={closeDoom}>
          <Doom restoreFocusTo={restoreFocusTo} onDone={closeDoom} />
        </Sentry.ErrorBoundary>
      )}
      {Game && (
        <Sentry.ErrorBoundary
          onError={() => {
            gameOpen.current = false;
            setGame(null);
          }}
        >
          <Game
            restoreFocusTo={restoreFocusTo}
            reducedMotion={
              window.matchMedia('(prefers-reduced-motion: reduce)').matches
            }
            onDone={() => {
              gameOpen.current = false;
              setGame(null);
            }}
          />
        </Sentry.ErrorBoundary>
      )}
    </>
  );
}
